import { LinkOutlined } from "@ant-design/icons";
import { Alert, Card } from "antd";
import ErrorBoundary from "components/error-boundary";
import {
  ESubmittalStatus,
  EWorkflowStatusDataBlock,
  ProjectPermissionEnum
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import {
  TFeatureWorkflowInstance,
  TFetureDetailUpdateAction
} from "entity-app/models";
import EntityLeadTimeView from "entity-app/shared-components/entity-dateblock/components/entity-lead-time/entity-lead-time-view";
import EntityRiskStatementView from "entity-app/shared-components/entity-dateblock/components/entity-risk-statement/entity-risk-statement-view";
import DateblockSubmittalMaterialEntity, {
  resolveMaterialGoverningSource,
  resolveSubmittalGoverningSource
} from "entity-app/shared-components/entity-dateblock/submittal-material-dateblock";
import EntityDateblockNewInterface from "entity-app/shared-components/entity-dateblock/entity-dateblock-new-interface";
import { TGoverningSource } from "entity-app/shared-components/entity-dateblock/models";
import ProjectWorkflowTemplateDropdown from "pages/preconstruction-details/components/workflow-template-dropdown";
import DateblockViewModeToggle, {
  DateblockViewMode,
  useDateblockViewMode
} from "entity-app/shared-components/entity-dateblock/dateblock-view-mode-toggle";
import { useContext, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

function HeadingDateBlock(
  props: Readonly<{
    featureInstanceData: any;
    actions: TFetureDetailUpdateAction;
    isRevisionMode: boolean;
    disableDB?: boolean;
    isRecalculating?: boolean;
    onRecalculatingChange?: (val: boolean) => void;
    viewMode: DateblockViewMode;
  }>
) {
  const { projectId } = useParams() as any;
  const {
    tokenContents,
    columnHeaders: { materialHeaderMap }
  } = useContext(ProjectContext);
  const {
    featureInstanceData,
    actions,
    isRevisionMode,
    disableDB,
    isRecalculating,
    onRecalculatingChange,
    viewMode
  } = props;
  if (!featureInstanceData) return null;
  const { feature_workflow_instance: featureWorkflowInstance } =
    featureInstanceData as {
      feature_workflow_instance: TFeatureWorkflowInstance;
    };

  const canOpenLinkingPage = !isPermissionNotGrantted(
    ProjectPermissionEnum.ViewLinkingPage,
    tokenContents?.role ?? ""
  );

  const isMaterial =
    featureInstanceData.feature_type_id === FeatureTypes.MATERIALS;
  const title = isMaterial ? "Material Schedule" : "Submittal Schedule";
  const to = isMaterial
    ? `/project/${projectId}/schedule/material-linking?materialId=${featureInstanceData.id}`
    : `/project/${projectId}/schedule/submittal-linking?SubmittalId=${featureInstanceData.id}`;

  return (
    <div className="flex w-full items-center" data-testid="slts-heading-root">
      <div className="flex items-center space-x-4">
        <div data-testid="slts-title-section">
          {title}
          {!disableDB && canOpenLinkingPage && (
            <Link
              to={to}
              title="Add Linking"
              data-testid="slts-link-add-linking"
            >
              <LinkOutlined
                style={{
                  color: "var(--ciq-blue-600)",
                  fontSize: 16,
                  marginLeft: 10,
                  marginTop: 2
                }}
                data-testid="slts-icon-link"
              />
            </Link>
          )}
        </div>
        {isRevisionMode ? (
          <span
            className="submittal--warning-message"
            data-testid="slts-warning-message"
          >
            To view the material schedule of the linked materials, please
            navigate to the latest revision.
          </span>
        ) : (
          ""
        )}
        {isMaterial && (
          <div className="pl-3" data-testid="slts-lead-time-container">
            <EntityLeadTimeView
              featureWorkflowInstance={featureInstanceData}
              materialHeaderMap={materialHeaderMap}
              data-testid="slts-lead-time-view"
            />
          </div>
        )}
        {!disableDB && (
          <div
            className="pl-3 text-ciq-gray-950 text-opacity-80"
            data-testid="slts-risk-statement-container"
          >
            <EntityRiskStatementView
              actions={actions}
              featureWorkflowInstance={featureWorkflowInstance}
              data-testid="slts-risk-statement-view"
            />
          </div>
        )}
        {isMaterial && !disableDB && viewMode === "new" && (
          <div
            className="flex items-center space-x-1 font-medium pl-3"
            data-testid="slts-template-dropdown-container"
          >
            <div
              className="text-ciq-gray-800 text-opacity-80 text-sm font-medium"
              data-testid="slts-template-label"
            >
              Template:
            </div>
            <ProjectWorkflowTemplateDropdown
              className="w-96"
              afterChange={() => actions.refetchFeatureInstanceData()}
              featureId={FeatureTypes.MATERIALS}
              featureWorkflowInstance={featureWorkflowInstance}
              setApiCallLoading={onRecalculatingChange}
              disable={!!isRecalculating}
              data-testid="slts-template-dropdown"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SubmittalLinkedTopSection(
  props: Readonly<{
    featureInstanceData: any;
    actions: TFetureDetailUpdateAction;
    isRevisionMode?: boolean;
    disableDB?: boolean;
    isDateblockRecalculating?: boolean;
    setIsDateblockRecalculating?: (val: boolean) => void;
  }>
) {
  const {
    featureInstanceData,
    actions,
    isRevisionMode,
    disableDB,
    isDateblockRecalculating,
    setIsDateblockRecalculating
  } = props;

  const {
    tokenContents,
    isFeatureFlagEnabled,
    columnHeaders: { submittalHeaderMap, materialHeaderMap }
  } = useContext(ProjectContext);
  const isFocalEntityEnabled = isFeatureFlagEnabled("FOCAL_ENTITY");

  const [internalRecalculating, setInternalRecalculating] = useState(false);
  const effectiveRecalculating =
    !!isDateblockRecalculating || internalRecalculating;
  const handleRecalculatingChange = (val: boolean) => {
    setInternalRecalculating(val);
    setIsDateblockRecalculating?.(val);
  };

  const isVoidEntity =
    featureInstanceData?.feature_workflow_instance?.workflow_status ===
    EWorkflowStatusDataBlock.VOID;
  const isDeletedEntity = featureInstanceData?.deleted_from_source;

  const storedViewMode = useDateblockViewMode();
  const dbDisabled =
    !!disableDB ||
    isVoidEntity ||
    !!isDeletedEntity ||
    !!isRevisionMode ||
    !featureInstanceData?.is_tracked;

  const isMaterial =
    featureInstanceData?.feature_type_id === FeatureTypes.MATERIALS;
  const isSubmittal =
    featureInstanceData?.feature?.feature === "SUBMITTALS" ||
    featureInstanceData?.feature_type_id === FeatureTypes.SUBMITTALS ||
    featureInstanceData?.feature_type_id ===
      FeatureTypes.PLANNING_ONLY_SUBMITTAL;

  const role = tokenContents?.role ?? "";
  const isActionBlocked = (permission: ProjectPermissionEnum) =>
    dbDisabled || isPermissionNotGrantted(permission, role);

  const cannotChangeDurationSubmittalDB = isActionBlocked(
    ProjectPermissionEnum.EditDateBlockOffsetSubmittal
  );

  const cannotChangeDurationMaterialDB = isActionBlocked(
    ProjectPermissionEnum.EditMaterialDBOffset
  );

  const cannotChangePlannedDate = isActionBlocked(
    ProjectPermissionEnum.changeMaterialPlannedDate
  );

  const cannotChangeActualDate = isActionBlocked(
    ProjectPermissionEnum.changeMaterialActualDate
  );

  const cannotChangeProjectedDate = isActionBlocked(
    ProjectPermissionEnum.EditDateblockProjectedDate
  );

  const canSubmittalOffsetBeEdited = useMemo(() => {
    if (!featureInstanceData || isMaterial) return false;
    const featureSubmittal: any =
      featureInstanceData?.feature_submittals?.length &&
      featureInstanceData.feature_submittals[0];

    return (
      !featureInstanceData?.deleted_from_source &&
      !!featureSubmittal?.workflow_status &&
      featureSubmittal.workflow_status !== ESubmittalStatus.VOID
    );
  }, [featureInstanceData, isMaterial]);

  const isMaterialDependedEntityInProgress = useMemo(() => {
    if (!isMaterial) return false;
    return (featureInstanceData?.links_as_target ?? []).some(
      (link: {
        source_feature_instance?: {
          feature_type_id?: number;
          feature_workflow_instance?: { workflow_status?: string };
        };
      }) =>
        link?.source_feature_instance?.feature_type_id ===
          FeatureTypes.SUBMITTALS &&
        [
          EWorkflowStatusDataBlock.IN_PROGRESS.toString(),
          EWorkflowStatusDataBlock.WF_NOT_STARTED.toString()
        ].includes(
          link?.source_feature_instance?.feature_workflow_instance
            ?.workflow_status ?? ""
        )
    );
  }, [featureInstanceData, isMaterial]);

  const arePredecessorsComplete =
    featureInstanceData?.feature_materials?.[0]?.ready_for_release;

  const governingSource: TGoverningSource | undefined = useMemo(() => {
    if (isMaterial) {
      return resolveMaterialGoverningSource(featureInstanceData)
        .governingSource;
    }
    return resolveSubmittalGoverningSource(featureInstanceData);
  }, [featureInstanceData, isMaterial]);

  const entityHeaders = isMaterial ? materialHeaderMap : submittalHeaderMap;

  const dateblockPermissions = useMemo(() => {
    if (dbDisabled || effectiveRecalculating) {
      return {};
    }

    if (isMaterial) {
      return {
        canEditActualDate: !isSubmittal && !cannotChangeActualDate,
        canEditProjectedDate: !isSubmittal && !cannotChangeProjectedDate,
        canEditDurations: !cannotChangeDurationMaterialDB,
        canEditPlannedDate: !cannotChangePlannedDate,
        isDependedEntityInProgress: isMaterialDependedEntityInProgress,
        arePredecessorsComplete
      };
    }

    return {
      canEditActualDate: false,
      canEditProjectedDate: isSubmittal && !cannotChangeProjectedDate,
      canEditDurations:
        !cannotChangeDurationSubmittalDB && canSubmittalOffsetBeEdited,
      canEditPlannedDate: !cannotChangePlannedDate,
      isDependedEntityInProgress: false,
      arePredecessorsComplete: true
    };
  }, [
    arePredecessorsComplete,
    canSubmittalOffsetBeEdited,
    cannotChangeActualDate,
    cannotChangeDurationMaterialDB,
    cannotChangeDurationSubmittalDB,
    cannotChangePlannedDate,
    cannotChangeProjectedDate,
    dbDisabled,
    effectiveRecalculating,
    isMaterial,
    isMaterialDependedEntityInProgress,
    isSubmittal
  ]);
  /**
   * Fall back to Full View only for states the Focused View does not support
   * (revision mode, untracked, or explicitly disabled). Void / deleted
   * entities stay on the user's stored preference and render read-only.
   */
  const forceClassicView =
    !isFocalEntityEnabled ||
    !!disableDB ||
    !!isRevisionMode ||
    !featureInstanceData?.is_tracked;
  const viewMode = forceClassicView ? "classic" : storedViewMode;

  return (
    <Card
      title={
        <HeadingDateBlock
          featureInstanceData={featureInstanceData}
          actions={actions}
          isRevisionMode={isRevisionMode || false}
          disableDB={
            disableDB ||
            isVoidEntity ||
            isDeletedEntity ||
            !featureInstanceData?.is_tracked
          }
          isRecalculating={effectiveRecalculating}
          onRecalculatingChange={handleRecalculatingChange}
          viewMode={viewMode}
        />
      }
      styles={{ body: { padding: 0 } }}
      className="ciq-card-block"
      data-testid="slts-card"
      extra={
        <div
          className="flex space-x-3 justify-end  h-10 items-center"
          data-testid="slts-extra"
        >
          <div data-testid="slts-governing-date-label">* Governing date</div>
          {isFocalEntityEnabled ? <DateblockViewModeToggle /> : null}
        </div>
      }
    >
      <div
        className={`w-full h-[470px] min-h-[470px] flex flex-col items-start ${
          viewMode === "new"
            ? "overflow-auto"
            : "overflow-x-auto overflow-y-hidden"
        }`}
        data-testid="slts-content-container"
      >
        {viewMode === "new" && isVoidEntity ? (
          <Alert
            className="w-full shrink-0 border-0 text-center bg-ciq-void-100"
            title={
              <div data-testid="slts-void-banner-content">
                This is <strong>Voided Submittal.</strong> Unlink Materials and
                Activities linked to this Submittal.
              </div>
            }
            type="info"
            data-testid="slts-void-alert"
          />
        ) : null}
        {viewMode === "new" && isDeletedEntity ? (
          <Alert
            className="w-full shrink-0 border-0 text-center bg-ciq-void-100"
            title={
              <div data-testid="slts-deleted-banner-content">
                This is a <strong>Deleted Submittal.</strong> Unlink Materials
                and Activties linked to this Submittal.
              </div>
            }
            type="info"
            data-testid="slts-deleted-alert"
          />
        ) : null}
        {viewMode === "new" ? (
          <ErrorBoundary>
            <EntityDateblockNewInterface
              entity={featureInstanceData}
              governingSource={governingSource}
              afterChange={() => {
                actions.refetchFeatureInstanceData();
              }}
              permissions={dateblockPermissions}
              setApiCallLoading={handleRecalculatingChange}
              entityHeaders={entityHeaders}
            />
          </ErrorBoundary>
        ) : (
          <div
            className="flex h-full min-h-0 w-full items-start"
            data-testid="slts-scroll-container"
          >
            <ErrorBoundary>
              <DateblockSubmittalMaterialEntity
                featureInstanceData={featureInstanceData}
                afterChange={() => {
                  actions.refetchFeatureInstanceData();
                }}
                disableDB={dbDisabled}
                isRecalculating={effectiveRecalculating}
                onRecalculatingChange={handleRecalculatingChange}
                data-testid="slts-dateblock-entity"
              />
            </ErrorBoundary>
          </div>
        )}
      </div>
    </Card>
  );
}
export default SubmittalLinkedTopSection;
